import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { useMyAuditLog } from '../hooks/useMyAuditLog';
import { AuditLogDrawer } from '../components/AuditLogDrawer';
import type {
  AuditLogListItem,
  AuditLogSearchCondition,
  ApiError,
  AuditResult,
} from '../types/auditLog';

const DEFAULT_PAGE_SIZE = 20;

type MyFilters = Omit<AuditLogSearchCondition, 'actor' | 'result' | 'severity' | 'resourceId' | 'traceId' | 'keyword'>;

function resultTone(result: AuditResult): StatusTone {
  if (result === 'SUCCESS') return 'success';
  if (result === 'DENY') return 'warning';
  return 'danger';
}

/** locale-aware 날짜/시간 포맷 (R7-096 fix) */
function formatDateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** Skeleton row */
function SkeletonRow() {
  return (
    <tr>
      {[60, 55, 45, 40].map((w, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  outline: 'none',
};

/**
 * MyAuditLogPage — 본인 활동 기록 (모든 인증 사용자)
 * /audit-logs/me 라우트
 */
export function MyAuditLogPage() {
  const { t, i18n } = useTranslation();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(0);

  const [appliedFilters, setAppliedFilters] = useState<MyFilters>({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sort: 'occurredAt,desc',
  });

  const [selectedId, setSelectedId] = useState<number | null>(null);

  const listQuery = useMyAuditLog(appliedFilters);
  const pageData = listQuery.data;

  const TABLE_HEADERS = [
    t('auditLog.list.column.occurredAt'),
    t('auditLog.list.column.action'),
    t('auditLog.list.column.resource'),
    t('auditLog.list.column.ip'),
  ];

  const handleSearch = useCallback(() => {
    const next: MyFilters = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sort: 'occurredAt,desc',
    };
    if (fromDate) next.from = `${fromDate}T00:00:00Z`;
    if (toDate) next.to = `${toDate}T23:59:59Z`;
    if (actionFilter) next.action = actionFilter;
    setPage(0);
    setAppliedFilters(next);
  }, [fromDate, toDate, actionFilter]);

  const handleReset = useCallback(() => {
    setFromDate('');
    setToDate('');
    setActionFilter('');
    setPage(0);
    setAppliedFilters({ page: 0, size: DEFAULT_PAGE_SIZE, sort: 'occurredAt,desc' });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowClick = useCallback((row: AuditLogListItem) => {
    setSelectedId(row.id);
  }, []);

  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && (pageData?.empty ?? false);
  const showTable = !isListLoading && !isListError && !isEmpty && !!pageData;

  return (
    <div>
      {/* Page header */}
      <PageHeader
        title={t('auditLog.me.title')}
        breadcrumb={<span>{t('auditLog.me.title')}</span>}
      />

      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {t('auditLog.me.summary', { total: pageData.totalElements, days: 30 })}
        </p>
      )}

      {/* Search filter bar */}
      <div
        role="search"
        aria-label={t('auditLog.me.filterAriaLabel')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          alignItems: 'flex-end',
          padding: 'var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="my-filter-from"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('auditLog.list.filter.from')}
          </label>
          <input
            id="my-filter-from"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={inputStyle}
            aria-label={t('auditLog.list.filter.from')}
          />
        </div>

        <span
          style={{
            paddingBottom: 'var(--spacing-2)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
          aria-hidden="true"
        >
          ~
        </span>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="my-filter-to"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('auditLog.list.filter.to')}
          </label>
          <input
            id="my-filter-to"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={inputStyle}
            aria-label={t('auditLog.list.filter.to')}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="my-filter-action"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('auditLog.list.filter.action')}
          </label>
          <select
            id="my-filter-action"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="">{t('auditLog.list.filter.actionAll')}</option>
            <option value="LOGIN">{t('auditLog.action.LOGIN')}</option>
            <option value="LOGOUT">{t('auditLog.action.LOGOUT')}</option>
            <option value="PASSWORD_CHANGED">{t('auditLog.action.PASSWORD_CHANGED')}</option>
            <option value="FILE_DOWNLOADED">{t('auditLog.action.FILE_DOWNLOADED')}</option>
            <option value="FILE_UPLOADED">{t('auditLog.action.FILE_UPLOADED')}</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
            aria-label={t('auditLog.list.button.search')}
          >
            {t('auditLog.list.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
            aria-label={t('auditLog.list.button.reset')}
          >
            {t('auditLog.list.button.reset')}
          </button>
        </div>
      </div>

      {/* Content — 5 states */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* Loading */}
        {isListLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('auditLog.me.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </tbody>
          </table>
        )}

        {/* Error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('auditLog.me.error.loadFailed')}
              message={
                (listQuery.error as unknown as ApiError)?.message ??
                t('auditLog.error.network')
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* Empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('auditLog.me.empty.title')}
              description={t('auditLog.me.empty.description')}
            />
            <p
              style={{
                marginTop: 'var(--spacing-3)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                textAlign: 'center',
              }}
            >
              {t('auditLog.me.empty.body')}
            </p>
            <ul
              aria-label={t('auditLog.me.empty.title')}
              style={{
                marginTop: 'var(--spacing-4)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
                justifyContent: 'center',
                listStyle: 'none',
                padding: 0,
              }}
            >
              {(
                ['auth', 'user', 'permission', 'file', 'code', 'profile', 'system'] as const
              ).map((cat) => (
                <li
                  key={cat}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-3)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(`auditLog.me.empty.categories.${cat}`)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Default — table */}
        {showTable && pageData && (
          <>
            <div role="region" aria-label={t('auditLog.me.tableAriaLabel')} style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('auditLog.me.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      aria-label={t('auditLog.me.rowAriaLabel', { id: row.id })}
                      onClick={() => handleRowClick(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(row);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                        }}
                      >
                        {formatDateTime(row.occurredAt, i18n.language)}
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                          <span
                            style={{
                              fontFamily: 'var(--font-family-mono)',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 600,
                              color: 'var(--color-text-primary)',
                            }}
                          >
                            {t(`auditLog.action.${row.action}`, { defaultValue: row.action })}
                          </span>
                          <StatusPill tone={resultTone(row.result)}>
                            {t(`auditLog.result.${row.result}`)}
                          </StatusPill>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                        }}
                      >
                        {row.resourceType
                          ? `${row.resourceType}${row.resourceId ? `/${row.resourceId}` : ''}`
                          : '-'}
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {row.ip ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
              }}
            >
              <span
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
              >
                {t('auditLog.me.pagination.total', { total: pageData.totalElements })}
              </span>
              <nav aria-label={t('auditLog.list.pagination.nav')} style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('auditLog.list.pagination.prev')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('auditLog.list.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28,
                      height: 28,
                      padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background:
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-elevated)',
                      border: `1px solid ${
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-border)'
                      }`,
                      color:
                        i === pageData.number
                          ? 'var(--color-text-inverse)'
                          : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('auditLog.list.pagination.next')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* Drawer */}
      {selectedId !== null && (
        <AuditLogDrawer logId={selectedId} onClose={() => setSelectedId(null)} />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
